import {
    EventName,
    GpsFilteredData,
    GpsPoint,
    HalfEvent,
    HrData,
    HrPoint,
    IEventMap,
    MatchPhone,
    PeriodNames,
    Timings,
    UserActionsCheckList,
} from "refsix-js-models";
import { recalculateStats } from "./matchService";
import { UserProfile } from "../models/userProfile";
import {
    copyObject,
    GpsProcessingService,
    HeartRateProcessingService,
} from "refsix-core";
import { updateUserActionsCheckList } from "./userActionsCheckListService";
import { submitMatchAndTagVenue } from "./venueService";

/**
 * Whether the match's playing periods are settled: either skipped, or split
 * into more than a single period (by the timings screen or the watch).
 */
function matchTimingsDone(match: MatchPhone): boolean {
    if (match.timingsSkipped) return true;
    if (!match.matchEvents) return false;

    const halfEvents = Object.values(match.matchEvents).filter(
        (e) => e.eventName === EventName.half
    );
    return halfEvents.length > 1;
}

/**
 * Check whether a match needs its playing periods to be set up.
 * Returns true if the match has fitness data but no (or only a single) half event.
 */
export function matchNeedsTimingsSetup(match: MatchPhone): boolean {
    if (!(match.hasTracking || match.hasHeartRate)) return false;
    return !matchTimingsDone(match);
}

/**
 * Whether tapping a finished match should open the edit match screen rather
 * than the overview - i.e. the user hasn't been through it yet. Settled
 * timings mean they already have, including for matches from before
 * `detailsConfirmed` existed.
 */
export function shouldOpenEditMatchOnTap(match: MatchPhone): boolean {
    return (
        match.matchFinished &&
        !match.detailsConfirmed &&
        !matchTimingsDone(match)
    );
}

/**
 * The period names only ever produced by the calibration screen. A match
 * refereed live on the watch never has warm up / warm down periods, so these
 * are what tell the two apart - including for matches calibrated before this
 * feature existed, which a flag on the match could not do.
 */
const CALIBRATION_ONLY_PERIOD_NAMES: string[] = [
    PeriodNames.warmUp,
    PeriodNames.warmDown,
];

/** Whether this match's periods were produced by the calibration screen. */
export function wasCalibratedViaTimingsScreen(match: MatchPhone): boolean {
    if (!match.matchEvents) return false;

    return Object.values(match.matchEvents).some(
        (event) =>
            event.eventName === EventName.half &&
            CALIBRATION_ONLY_PERIOD_NAMES.includes((event as HalfEvent).name)
    );
}

/**
 * Whether to offer "adjust playing periods" for a match that has already been
 * through - or deliberately skipped - the calibration screen. Without this the
 * screen is unreachable after the first save, so a mis-tapped Save is stuck.
 *
 * Deliberately false for matches refereed live on the watch: their half events
 * are real timing data, not a guess to be redone.
 */
export function canAdjustPlayingPeriods(match: MatchPhone): boolean {
    if (!(match.hasTracking || match.hasHeartRate)) return false;
    // Already being prompted automatically - no need for a menu item too.
    if (matchNeedsTimingsSetup(match)) return false;

    return (
        match.timingsSkipped === true || wasCalibratedViaTimingsScreen(match)
    );
}

/**
 * Whether the one-time "how to set your playing periods" tutorial should be
 * played automatically. Only for users who have never saved playing periods
 * before - afterwards it is available on demand via the (i) icon.
 */
export function shouldShowPlayingPeriodsTutorial(
    match: MatchPhone,
    userActionsCheckList: UserActionsCheckList
): boolean {
    return (
        matchNeedsTimingsSetup(match) &&
        !userActionsCheckList?.hasCalibratedPlayingPeriods
    );
}

/**
 * Create 5 HalfEvent objects from 4 slider positions.
 *
 * | Index | Name        | playing | Start           | End             |
 * |-------|-------------|---------|-----------------|-----------------|
 * | 0     | Warm Up     | false   | activity start  | slider[0]       |
 * | 1     | First Half  | true    | slider[0]       | slider[1]       |
 * | 2     | Half Time   | false   | slider[1]       | slider[2]       |
 * | 3     | Second Half | true    | slider[2]       | slider[3]       |
 * | 4     | Warm Down   | false   | slider[3]       | activity end    |
 */
export function createHalfEventsFromSliders(
    positions: number[],
    startTime: number,
    endTime: number
): HalfEvent[] {
    const totalDuration = endTime - startTime;

    const sliderTimes = positions.map((pos) => startTime + pos * totalDuration);

    const periods: {
        name: string;
        playing: boolean;
        start: number;
        end: number;
    }[] = [
        {
            name: PeriodNames.warmUp,
            playing: false,
            start: startTime,
            end: sliderTimes[0],
        },
        {
            name: PeriodNames.firstHalf,
            playing: true,
            start: sliderTimes[0],
            end: sliderTimes[1],
        },
        {
            name: PeriodNames.halfTime,
            playing: false,
            start: sliderTimes[1],
            end: sliderTimes[2],
        },
        {
            name: PeriodNames.secondHalf,
            playing: true,
            start: sliderTimes[2],
            end: sliderTimes[3],
        },
        {
            name: PeriodNames.warmDown,
            playing: false,
            start: sliderTimes[3],
            end: endTime,
        },
    ];

    return periods.map(
        (p, index) =>
            new HalfEvent(
                Math.round(p.start),
                EventName.half,
                index, // half
                0, // minuteOfPlay
                index, // index
                p.name,
                p.playing,
                Math.round(p.end - p.start), // length in millis
                undefined, // kickOffSide
                0, // injuryTimeAccumulated
                0, // injuryTimeInInjuryTimeAccumulated
                undefined, // endMinute
                Math.round(p.end), // endTime
                false // isExtraTime
            )
    );
}

/**
 * The inverse of createHalfEventsFromSliders: recover the 4 slider fractions
 * from a match that has already been calibrated, so re-opening the screen
 * shows where the periods actually are rather than the defaults.
 *
 * Returns undefined when the match was not calibrated here, the activity
 * range is degenerate, or the saved periods fall outside the range - the
 * caller then falls back to the default positions.
 */
export function sliderPositionsFromHalfEvents(
    match: MatchPhone,
    startTime: number,
    endTime: number
): number[] | undefined {
    const totalDuration = endTime - startTime;
    if (totalDuration <= 0) return undefined;
    if (!wasCalibratedViaTimingsScreen(match) || !match.matchEvents) {
        return undefined;
    }

    const byName = (name: PeriodNames): HalfEvent | undefined =>
        Object.values(match.matchEvents!).find(
            (event) =>
                event.eventName === EventName.half &&
                (event as HalfEvent).name === name
        ) as HalfEvent | undefined;

    const firstHalf = byName(PeriodNames.firstHalf);
    const secondHalf = byName(PeriodNames.secondHalf);
    if (!firstHalf?.endTime || !secondHalf?.endTime) return undefined;

    const positions = [
        firstHalf.timestamp,
        firstHalf.endTime,
        secondHalf.timestamp,
        secondHalf.endTime,
    ].map((timestamp) => (timestamp - startTime) / totalDuration);

    const isUsable =
        positions.every((pos) => pos >= 0 && pos <= 1) &&
        positions.every((pos, i) => i === 0 || pos >= positions[i - 1]);

    return isUsable ? positions : undefined;
}

/**
 * Move all goal timestamps to firstHalfStartTime + offset.
 * Each goal gets a unique offset (1ms apart).
 *
 * `half` must match the period the goals are being moved into, or anything
 * keyed on it - sin bin goal attribution, for one - silently stops matching.
 * It defaults to 1, the First Half written by createHalfEventsFromSliders.
 */
export function moveGoalTimestamps(
    matchEvents: IEventMap,
    firstHalfStartTime: number,
    half: number = 1
): IEventMap {
    const newEvents: IEventMap = {};
    let goalOffset = 0;

    for (const [key, event] of Object.entries(matchEvents)) {
        if (event.eventName === EventName.goal) {
            const newTimestamp = firstHalfStartTime + 60000 + goalOffset;
            const movedEvent = {
                ...event,
                timestamp: newTimestamp,
                half,
                minuteOfPlay: 1,
            };
            newEvents[String(newTimestamp)] = movedEvent;
            goalOffset += 1; // 1ms offset for unique keys
        } else if (event.eventName !== EventName.half) {
            // Keep non-half, non-goal events as-is
            newEvents[key] = event;
        }
        // Drop existing half events – they'll be replaced
    }

    return newEvents;
}

/** Length of each period the user chose, in whole minutes. */
export interface SavedPeriodLengths {
    warmUp: number;
    period1: number;
    break1: number;
    period2: number;
    warmDown: number;
}

/**
 * Full save flow: creates half events from slider positions,
 * moves goals, updates match timings, and recalculates stats.
 * Returns the chosen period lengths in minutes.
 */
export async function saveMatchTimings(
    match: MatchPhone,
    positions: number[],
    startTime: number,
    endTime: number,
    profile: UserProfile
): Promise<SavedPeriodLengths> {
    const halfEvents = createHalfEventsFromSliders(
        positions,
        startTime,
        endTime
    );

    // Move goal timestamps to 1st half
    const firstHalfStart = halfEvents[1].timestamp; // index 1 = First Half
    let newEvents: IEventMap = moveGoalTimestamps(
        match.matchEvents || {},
        firstHalfStart
    );

    // Add new half events keyed by timestamp
    for (const he of halfEvents) {
        newEvents[String(he.timestamp)] = he;
    }

    // Clone match to avoid mutating Redux state
    const updatedMatch = copyObject(match);
    updatedMatch.matchEvents = newEvents;

    // Set periods and timings (convert ms to minutes)
    const period1Ms = halfEvents[1].length; // First Half duration
    const period2Ms = halfEvents[3].length; // Second Half duration
    const interval1Ms = halfEvents[2].length; // Half Time duration

    updatedMatch.periodsNo = "2";
    updatedMatch.timings = new Timings(
        period1Ms / 60000,
        period2Ms / 60000,
        interval1Ms / 60000
    );
    updatedMatch.matchFinished = true;

    await recalculateStats(updatedMatch, profile, true);

    // SCORZA-739. Only now are the stats final: the periods above decide which GPS points
    // count, so a submission before this point publishes numbers that are about to change -
    // and for an uncalibrated match it would match the venue on the wrong centre point too.
    // Awaited so the ribbons are right by the time the screen closes.
    await submitMatchAndTagVenue(updatedMatch);

    // The user has now calibrated playing periods at least once, so the
    // tutorial no longer plays automatically (SCORZA-573).
    updateUserActionsCheckList({ hasCalibratedPlayingPeriods: true });

    const toMinutes = (millis: number) => Math.round(millis / 60000);
    return {
        warmUp: toMinutes(halfEvents[0].length),
        period1: toMinutes(period1Ms),
        break1: toMinutes(interval1Ms),
        period2: toMinutes(period2Ms),
        warmDown: toMinutes(halfEvents[4].length),
    };
}

/**
 * A single playing period covering the whole tracked activity - i.e. the
 * match as it was before anyone split it into halves.
 */
export function createSinglePeriodHalfEvent(
    startTime: number,
    endTime: number
): HalfEvent {
    const start = Math.round(startTime);
    const end = Math.round(endTime);

    return new HalfEvent(
        start,
        EventName.half,
        0, // half
        0, // minuteOfPlay
        0, // index
        PeriodNames.firstPeriod,
        true, // playing
        end - start, // length in millis
        undefined, // kickOffSide
        0, // injuryTimeAccumulated
        0, // injuryTimeInInjuryTimeAccumulated
        undefined, // endMinute
        end,
        false // isExtraTime
    );
}

/**
 * Undo a calibration: collapse the match back to a single period spanning the
 * tracked activity, from the first to the last fitness data point.
 *
 * timingsSkipped is set so the calibration screen does not auto-open again on
 * the next visit - it stays reachable from the match options menu.
 */
export async function resetMatchTimings(
    match: MatchPhone,
    startTime: number,
    endTime: number,
    profile: UserProfile
): Promise<void> {
    const updatedMatch = collapseToSinglePeriod(match, startTime, endTime);
    await recalculateStats(updatedMatch, profile, true);
    await submitMatchAndTagVenue(updatedMatch);
}

/** A copy of the match with its periods collapsed into one from start to end. */
function collapseToSinglePeriod(
    match: MatchPhone,
    startTime: number,
    endTime: number
): MatchPhone {
    const singlePeriod = createSinglePeriodHalfEvent(startTime, endTime);

    // Keeps goals inside the period and drops the old half events. The
    // collapsed period is half 0, not the First Half's 1.
    const newEvents: IEventMap = moveGoalTimestamps(
        match.matchEvents || {},
        singlePeriod.timestamp,
        singlePeriod.half
    );
    newEvents[String(singlePeriod.timestamp)] = singlePeriod;

    const updatedMatch = copyObject(match);
    updatedMatch.matchEvents = newEvents;
    updatedMatch.periodsNo = "1";
    updatedMatch.timings = new Timings(singlePeriod.length / 60000);
    updatedMatch.matchFinished = true;
    updatedMatch.timingsSkipped = true;
    return updatedMatch;
}

/**
 * The time the match's events cover: the outer bounds of its periods, or of
 * all its events when it has none. A calibrated match's periods are bounded by
 * the tracked activity, so this recovers that range without the fitness data.
 */
function matchEventsSpan(
    match: MatchPhone
): { start: number; end: number } | undefined {
    const events = Object.values(match.matchEvents || {});
    const halves = events.filter(
        (e) => e.eventName === EventName.half
    ) as HalfEvent[];
    const bounded = halves.length > 0 ? halves : events;
    if (bounded.length === 0) return undefined;

    const start = Math.min(...bounded.map((e) => e.timestamp));
    const end = Math.max(
        ...bounded.map((e) => {
            const half = e as HalfEvent;
            return half.endTime ?? e.timestamp + (half.length || 0);
        })
    );
    return end > start ? { start, end } : undefined;
}

/**
 * A copy of the match marked as a training session. Training has no halves,
 * so its events collapse into one big period and the timings screen is never
 * offered.
 */
export function markAsTraining(match: MatchPhone): MatchPhone {
    const span = matchEventsSpan(match);
    const training = span
        ? collapseToSinglePeriod(match, span.start, span.end)
        : copyObject(match);
    training.isTraining = true;
    training.timingsSkipped = true;
    return training;
}

/**
 * What a skip actually did. The two outcomes are materially different, and the
 * caller cannot infer which happened from its own arguments - it also depends
 * on whether the match was calibrated here in the first place.
 */
export interface SkipOutcome {
    /** True when the periods were collapsed back to one, false for a plain skip. */
    reset: boolean;
}

/**
 * Skipping means "I don't want calibrated periods on this match".
 *
 * On a first run there is nothing to undo, so this only records the skip. On a
 * match that has already been calibrated it also resets the periods, otherwise
 * the previously saved (often accidental) halves would silently survive.
 */
export async function skipMatchTimings(
    match: MatchPhone,
    profile: UserProfile,
    startTime?: number,
    endTime?: number
): Promise<SkipOutcome> {
    const canReset =
        startTime !== undefined &&
        endTime !== undefined &&
        endTime > startTime &&
        wasCalibratedViaTimingsScreen(match);

    if (canReset) {
        await resetMatchTimings(match, startTime!, endTime!, profile);
        return { reset: true };
    }

    const updatedMatch = copyObject(match);
    updatedMatch.timingsSkipped = true;
    await recalculateStats(updatedMatch, profile, true);
    await submitMatchAndTagVenue(updatedMatch);
    return { reset: false };
}

/**
 * The basic null/malformed-point filtering from the stats pipeline, without the
 * during-play gating (the timings screen is what creates the play periods) or
 * any accuracy/speed filtering and smoothing.
 */
export function cleanHrDataForTimings(hrData: HrData): HrData {
    const sorted = new HeartRateProcessingService().sortHeartRate({
        heartRateValues: [...(hrData.heartRateValues || [])],
    }).heartRateValues as HrPoint[];
    const heartRateValues = sorted.filter(
        (point, i) =>
            i === 0 ||
            point.time !== sorted[i - 1].time ||
            point.value !== sorted[i - 1].value
    );
    return { ...hrData, heartRateValues };
}

export function cleanGpsDataForTimings(
    gpsData: GpsFilteredData
): GpsFilteredData {
    const service = new GpsProcessingService();
    const sorted = service.filterOutInvalidPoints(
        service.sortGpsData({ geoPoints: [...(gpsData.geoPoints || [])] })
            .geoPoints as GpsPoint[]
    );
    const geoPoints = sorted.filter(
        (point, i) => i === 0 || point.time !== sorted[i - 1].time
    );
    return { ...gpsData, geoPoints };
}
